import { useEffect, useMemo, useRef, useState } from 'react'
import { getImageUrlFromStreamMetadata, ImageSize } from 'api/lib/fetchImage'
import { useImageStore } from 'use-towns-client'

export const ImageVariants = {
    thumbnail100: 'thumbnail100',
    thumbnail50: 'thumbnail50',
    thumbnail300: 'thumbnail300',
    thumbnail600: 'thumbnail600',
    thumbnail1200: 'thumbnail1200',
} as const

export type ImageVariant = keyof typeof ImageVariants

const ImageVariantSizes: Record<ImageVariant, ImageSize> = {
    [ImageVariants.thumbnail100]: '100x100',
    [ImageVariants.thumbnail50]: '50x50',
    [ImageVariants.thumbnail300]: '300x300',
    [ImageVariants.thumbnail600]: '600x600',
    [ImageVariants.thumbnail1200]: '1200x1200',
} as const

export const useImageSource = (resourceId: string, imageVariant: ImageVariant) => {
    const storedImageUrl = useImageStore((state) => state.loadedResource[resourceId]?.imageUrl)
    const externalImageUrl = getImageUrlFromStreamMetadata(resourceId)

    const resizedUrl = useMemo(() => {
        const imageSize = ImageVariantSizes[imageVariant]
        const baseUrl = storedImageUrl || externalImageUrl
        if (!baseUrl) {
            return undefined
        }
        try {
            const resizedUrl = new URL(baseUrl)
            resizedUrl.searchParams.set('size', imageSize)
            return resizedUrl.toString()
        } catch (error) {
            console.warn(`Error formatting image url for resourceId: ${resourceId}`, error)
            return baseUrl
        }
    }, [imageVariant, storedImageUrl, externalImageUrl, resourceId])

    const [isLoaded, setImageLoaded] = useState(false)
    const [isError, setImageError] = useState<boolean | undefined>(undefined)
    const isLoading = !isLoaded && !isError

    const onLoad = () => {
        const imageUrl = storedImageUrl || externalImageUrl
        if (imageUrl) {
            useImageStore.getState().setLoadedResource(resourceId, { imageUrl })
        }

        setImageLoaded(true)
    }

    const onError = () => {
        useImageStore.getState().addErroredResource(resourceId)
        setImageError(true)
    }

    const storedImageUrlRef = useRef(storedImageUrl)

    // reset image loading when a new image is uploaded
    useEffect(() => {
        if (storedImageUrlRef.current === storedImageUrl) {
            // prevent reset on initial mount, otherwise state might get reset wrongly
            return
        }
        if (storedImageUrl === externalImageUrl) {
            storedImageUrlRef.current = storedImageUrl
        }
        if (storedImageUrl !== externalImageUrl && storedImageUrl !== storedImageUrlRef.current) {
            storedImageUrlRef.current = storedImageUrl
            setImageLoaded(false)
            setImageError(undefined)
        }
    }, [externalImageUrl, storedImageUrl])

    return {
        isLoaded,
        onLoad,
        onError,
        isError,
        isLoading,
        // TODO: temporary cache busting
        // new images eventually show up, but the browser caches them and users who upload anything might see their old image for a while, even after refreshing
        // We're calling images directly from imagedelivery.net, we can't modify those headers unless we use some proxy - will need to revisit the CF worker or add a service worker interceptor
        imageSrc: resizedUrl,
    }
}
